import { DatePipe } from '@angular/common';
import { Pipe, PipeTransform } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

const intervals = {
  day: 86400,
  hour: 3600,
  minute: 60,
  second: 1,
};

@Pipe({
  name: 'DateTime',
  pure: true,
})
export class DateTimePipe implements PipeTransform {
  constructor(
    private readonly datePipe: DatePipe,
    private readonly translocoService: TranslocoService
  ) {}

  // Check if date is withing current day
  isToday(dateParam: string): boolean {
    const today = new Date();
    const incomingDate = new Date(dateParam);

    return (
      incomingDate.getDate() === today.getDate() &&
      incomingDate.getMonth() === today.getMonth() &&
      incomingDate.getFullYear() === today.getFullYear()
    );
  }

  //Check if date is yesterday
  isYesterday(dateParams: string): boolean {
    const currentDate = new Date(dateParams);
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    return currentDate.toDateString() === yesterday.toDateString();
  }

  padTo2Digits(num: number): string {
    return String(num).padStart(2, '0');
  }

  transform(value: string, format?: string): string | null {
    const seconds = Math.floor((+new Date() - +new Date(value)) / 1000);

    if (seconds < 30) {
      return this.translocoService.translate('notifications.userNotifications.date.justNow');
    } else if (seconds < 60) {
      return this.translocoService.translate('notifications.userNotifications.date.seconds', { seconds });
    }

    if (this.isToday(value)) {
      const hours = Math.floor(seconds / intervals.hour);
      if (hours === 0) {
        const minutes = Math.floor(seconds / intervals.minute);
        return this.translocoService.translate(
          `notifications.userNotifications.date.minute${this.checkSingularity(minutes)}`,
          { minutes }
        );
      } else {
        return this.translocoService.translate(
          `notifications.userNotifications.date.hour${this.checkSingularity(hours)}`,
          { hours }
        );
      }
    }

    if (this.isYesterday(value)) {
      const hours = this.padTo2Digits(new Date(value).getHours());
      const minutes = this.padTo2Digits(new Date(value).getMinutes());

      return this.translocoService.translate('notifications.userNotifications.date.yesterday', { hours, minutes });
    }

    const days = seconds / intervals.day;
    const weeks = seconds / (intervals.day * 7);

    // Between 2 and 6 days
    if (this.isInRange(days, 1, 6.5)) {
      let roundedDays = this.getRoundedValue(seconds, intervals.day);

      if (days > 1 && days < 1.5) {
        roundedDays = 2;
      }

      return this.translocoService.translate('notifications.userNotifications.date.days', { days: roundedDays });
    }

    //Between 1 and 3 weeks
    if (this.isInRange(weeks, 0.5, 3.5)) {
      const roundedWeeks = this.getRoundedValue(seconds, intervals.day * 7);
      const key = roundedWeeks === 1 ? 'week' : 'weeks';
      return this.translocoService.translate(`notifications.userNotifications.date.${key}`, { weeks: roundedWeeks });
    }

    return this.datePipe.transform(value, format);
  }

  private isInRange(value: number, min: number, max: number): boolean {
    return value >= min && value < max;
  }

  private getRoundedValue(seconds: number, interval: number): number {
    const numberString: string = (seconds / interval).toString();
    const decimalPart: string = numberString.split('.')[1] || '';
    return Number(decimalPart.substring(0, 2)) >= 50 ? Math.ceil(seconds / interval) : Math.floor(seconds / interval);
  }

  private checkSingularity(value: number): string {
    if (value > 1) {
      return 's';
    }
    return '';
  }
}
